import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Receipt, CheckCircle2, ChevronLeft, ChevronRight, List, Columns } from "lucide-react";
import api from "../../lib/api";
import { PeriodPicker } from "./PeriodPicker";
import { AdminBookingModal } from "./AdminBookingModal";

const BOOKING_STATUSES = ["confirmada", "concluída", "cancelada"];

const BOOKING_ROW_COLORS = {
  confirmada: "bg-sky-100 border-l-4 border-l-sky-600",
  "concluída": "bg-emerald-100 border-l-4 border-l-emerald-600",
  cancelada: "bg-red-100 border-l-4 border-l-red-500",
};

const SourceBadge = ({ source }) => {
  const interna = source === "interna";
  return (
    <span
      data-testid={`booking-source-${interna ? "interna" : "site"}`}
      className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border ${
        interna
          ? "bg-clay/10 border-clay/40 text-clay-dark"
          : "bg-black/5 border-black/15 text-black/60"
      }`}
    >
      {interna ? "Interna" : "Site"}
    </span>
  );
};

const ConvertCell = ({ b, onConvert }) =>
  b.converted_sale_id ? (
    <span data-testid={`booking-converted-${b.id}`} className="inline-flex items-center gap-1 text-emerald-700 text-xs">
      <CheckCircle2 size={14} /> Convertida
    </span>
  ) : b.status === "cancelada" ? (
    <span className="text-black/25 text-xs">—</span>
  ) : (
    <button
      data-testid={`booking-convert-${b.id}`}
      onClick={() => onConvert && onConvert(b)}
      className="inline-flex items-center gap-1 text-xs border border-clay/40 text-clay-dark px-2 py-1 hover:bg-clay/10 transition-colors"
      title="Converter em venda (Folha de Caixa)"
    >
      <Receipt size={13} /> → Caixa
    </button>
  );

const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export const BookingsAdmin = ({ onConvert }) => {
  const [range, setRange] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [view, setView] = useState("lista"); // "lista" | "agenda"
  const [filterStaff, setFilterStaff] = useState("todos");
  const [filterSource, setFilterSource] = useState("todas");
  const [showCreate, setShowCreate] = useState(false);
  const [agendaDate, setAgendaDate] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    api.get("/admin/professionals").then((r) => setProfessionals(r.data || [])).catch(() => {});
  }, []);

  const loadList = useCallback(() => {
    if (view !== "lista" || !range) return;
    const params = range.start ? { start: range.start, end: range.end } : {};
    api.get("/admin/bookings", { params }).then((res) => setBookings(res.data)).catch(() => {});
  }, [range, view]);

  const loadAgenda = useCallback(() => {
    if (view !== "agenda") return;
    api
      .get("/admin/bookings", { params: { start: agendaDate, end: agendaDate } })
      .then((res) => setBookings(res.data))
      .catch(() => {});
  }, [view, agendaDate]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadAgenda();
  }, [loadAgenda]);

  const reload = () => (view === "agenda" ? loadAgenda() : loadList());

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    toast.success("Estado atualizado.");
    reload();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta marcação?")) return;
    await api.delete(`/admin/bookings/${id}`);
    toast.success("Marcação eliminada.");
    reload();
  };

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (filterStaff !== "todos" && (b.staff || "—") !== filterStaff) return false;
      if (filterSource === "site" && b.source === "interna") return false;
      if (filterSource === "interna" && b.source !== "interna") return false;
      return true;
    });
  }, [bookings, filterStaff, filterSource]);

  // Colunas de agenda: profissionais + qualquer staff presente nas marcações + "Sem profissional"
  const agendaColumns = useMemo(() => {
    const names = new Set(professionals.map((p) => p.name));
    bookings.forEach((b) => {
      if (b.staff) names.add(b.staff);
    });
    const cols = Array.from(names).sort();
    if (bookings.some((b) => !b.staff)) cols.push("—");
    return cols.length ? cols : ["—"];
  }, [professionals, bookings]);

  const bySlotColor = (b) => BOOKING_ROW_COLORS[b.status] || "bg-white";

  return (
    <div className="space-y-6">
      {/* Barra superior: criar + toggle de vista */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            data-testid="booking-view-lista"
            onClick={() => setView("lista")}
            className={`label-editorial px-4 py-2.5 border flex items-center gap-2 ${
              view === "lista" ? "bg-black text-white border-black" : "border-black/15 text-black/60 hover:bg-black/5"
            }`}
          >
            <List size={14} /> Lista
          </button>
          <button
            data-testid="booking-view-agenda"
            onClick={() => setView("agenda")}
            className={`label-editorial px-4 py-2.5 border flex items-center gap-2 ${
              view === "agenda" ? "bg-black text-white border-black" : "border-black/15 text-black/60 hover:bg-black/5"
            }`}
          >
            <Columns size={14} /> Agenda por profissional
          </button>
        </div>
        <button
          data-testid="booking-new-btn"
          onClick={() => setShowCreate(true)}
          className="label-editorial bg-clay text-white px-6 py-2.5 hover:bg-clay-dark transition-colors flex items-center gap-2"
        >
          <Plus size={14} /> Nova marcação
        </button>
      </div>

      {view === "lista" ? (
        <>
          <PeriodPicker onRange={setRange} allowAll defaultMode="todas" testPrefix="bookings-period" />

          {/* Filtros */}
          <div className="flex flex-wrap gap-3">
            <div>
              <label className="label-editorial text-black/40 block mb-1.5">Profissional</label>
              <select
                data-testid="booking-filter-staff"
                value={filterStaff}
                onChange={(e) => setFilterStaff(e.target.value)}
                className="bg-white border border-black/15 px-3 py-2 text-sm font-light focus:outline-none focus:border-black"
              >
                <option value="todos">Todos</option>
                {professionals.map((p) => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-editorial text-black/40 block mb-1.5">Origem</label>
              <select
                data-testid="booking-filter-source"
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                className="bg-white border border-black/15 px-3 py-2 text-sm font-light focus:outline-none focus:border-black"
              >
                <option value="todas">Todas</option>
                <option value="site">Site</option>
                <option value="interna">Interna</option>
              </select>
            </div>
          </div>

          <div className="bg-white border border-black/5 overflow-x-auto">
            <table data-testid="bookings-table" className="w-full text-sm font-light min-w-[900px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-black/40 border-b border-black/10">
                  <th className="p-4">Data</th>
                  <th className="p-4">Hora</th>
                  <th className="p-4">Cliente</th>
                  <th className="p-4">Contacto</th>
                  <th className="p-4">Serviço</th>
                  <th className="p-4">Profissional</th>
                  <th className="p-4">Origem</th>
                  <th className="p-4">Estado</th>
                  <th className="p-4">Venda</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={10} className="p-6 text-black/30">Sem marcações neste período.</td></tr>
                )}
                {filtered.map((b) => (
                  <tr
                    key={b.id}
                    data-testid={`booking-row-${b.id}`}
                    className={`border-b border-black/5 last:border-0 transition-colors duration-300 ${bySlotColor(b)}`}
                  >
                    <td className="p-4 whitespace-nowrap">{b.date}</td>
                    <td className="p-4">{b.time}</td>
                    <td className="p-4">{b.name}</td>
                    <td className="p-4 text-black/50">
                      {b.phone || "—"}
                      <br />
                      {b.email || ""}
                    </td>
                    <td className="p-4">
                      {b.service}
                      {b.duration_min ? <span className="text-black/40"> · {b.duration_min} min</span> : null}
                    </td>
                    <td className="p-4">{b.staff || "—"}</td>
                    <td className="p-4"><SourceBadge source={b.source} /></td>
                    <td className="p-4">
                      <select
                        data-testid={`booking-status-${b.id}`}
                        value={b.status}
                        onChange={(e) => updateStatus(b.id, e.target.value)}
                        className="bg-white border border-black/15 px-2 py-1.5 text-xs uppercase tracking-widest focus:outline-none focus:border-black"
                      >
                        {BOOKING_STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4"><ConvertCell b={b} onConvert={onConvert} /></td>
                    <td className="p-4">
                      <button data-testid={`booking-delete-${b.id}`} onClick={() => handleDelete(b.id)} className="p-1.5 hover:bg-black/5 text-red-600" aria-label="Eliminar">
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          {/* Navegação de dia */}
          <div className="flex items-center gap-3">
            <button
              data-testid="agenda-prev"
              onClick={() => setAgendaDate((d) => addDays(d, -1))}
              className="p-2 border border-black/15 hover:bg-black/5"
            >
              <ChevronLeft size={16} />
            </button>
            <input
              data-testid="agenda-date"
              type="date"
              value={agendaDate}
              onChange={(e) => setAgendaDate(e.target.value)}
              className="bg-white border border-black/15 px-3 py-2 text-sm font-light focus:outline-none focus:border-black"
            />
            <button
              data-testid="agenda-next"
              onClick={() => setAgendaDate((d) => addDays(d, 1))}
              className="p-2 border border-black/15 hover:bg-black/5"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div data-testid="agenda-grid" className="grid gap-4 overflow-x-auto" style={{ gridTemplateColumns: `repeat(${agendaColumns.length}, minmax(200px, 1fr))` }}>
            {agendaColumns.map((col) => {
              const items = bookings
                .filter((b) => (b.staff || "—") === col)
                .sort((a, b) => (a.time || "").localeCompare(b.time || ""));
              return (
                <div key={col} data-testid={`agenda-col-${col}`} className="bg-white border border-black/5 min-h-[120px]">
                  <div className="p-3 border-b border-black/5 label-editorial text-black/60 bg-white">
                    {col} <span className="text-black/30">({items.length})</span>
                  </div>
                  <div className="p-2 space-y-2">
                    {items.length === 0 && <p className="text-xs text-black/25 p-2">Sem marcações.</p>}
                    {items.map((b) => (
                      <div
                        key={b.id}
                        data-testid={`agenda-card-${b.id}`}
                        className={`p-3 border border-black/5 ${bySlotColor(b)}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{b.time}</span>
                          <SourceBadge source={b.source} />
                        </div>
                        <p className="text-sm mt-1">{b.name}</p>
                        <p className="text-xs text-black/50">{b.service} · {b.duration_min} min</p>
                        <div className="flex items-center justify-between mt-2 gap-2">
                          <span className="text-[10px] uppercase tracking-widest text-black/40">{b.status}</span>
                          <ConvertCell b={b} onConvert={onConvert} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {showCreate && (
        <AdminBookingModal onClose={() => setShowCreate(false)} onCreated={reload} />
      )}
    </div>
  );
};
